<template>
  <transition
    enter-active-class="animated fadeIn"
    leave-active-class="animated fadeOut"
    mode="out-in"
    @after-enter="unblockCheckout"
    @before-leave="blockCheckout"
  >
    <div
      class="small-12 column cart__continue-btn"
      :class="{ 'cart__continue-btn--loading': checkoutBlocked || !stockCheckComplete, 'cart__continue-btn--unavailable': !allInStock || !aboveMinInstantPayOrderThreshold}"
      v-if="normalCheckout"
      :key="1"
    >
      <div
        v-if="instantPayFailed || !aboveMinInstantPayOrderThreshold"
        class="instant-pay__error-message"
      >
        <div class="instant-pay__error-message-box">
          <div>
            <svg
              class="cart-product__warning-icon"
              width="16"
              height="16"
              viewBox="0 0 32 32"
            >
              <use xlink:href="#alert_circle" />
            </svg>
          </div>
          <p v-if="!aboveMinInstantPayOrderThreshold">Instant pay is not available for orders under £{{instantPayMinOrderThreshold}}. Please use our standard checkout.</p>
          <p v-else>Sorry, your instant payment couldn't be completed. Please try our standard checkout.</p>
        </div>
      </div>
      <div class="checkout-buttons-inner">
        <div :id="paymentRequestButtonId" :class="[(showExpressCheckout) ? 'instant-pay-button' : 'hide']">
          <!-- Stripe instant pay element will be inserted here. -->
        </div>
        <a :class="checkoutBtnClass" :href="getCheckoutUrl">
          <div>
            <span class="button__icon button__icon--checkout">
              <svg class="icon--padlock" width="15" height="17" viewBox="0 0 28 28">
                <use xlink:href="#icon-padlock" />
              </svg>
            </span>
            <span class="cart__checkout-button-text">{{ secureCheckoutText }}</span>
            <span class="cart__checkout-button-text--short">{{ secureCheckoutTextShort }}</span>
          </div>
        </a>
      </div>
    </div>
    <div
      class="cart__disabled-checkout-container"
      v-else
      :key="2"
    >
      <div>
        <errorMessage
          :show-error="showError"
          :error-text="errorText"
        />
      </div>
      <div v-if="belowMinimumThreshold">
        <p
          class="cart__disabled-checkout-text h6"
          :class="minimumOrderMessageClass"
        >
          {{ minOrderValueWarning }}
        </p>
        <div class="row">
          <div
            class="small-6 column cart__disabled-checkout-container-column"
            @click="closeMicrocartExtend"
          >
            <div class="button cart__keep-browsing-btn">
              <svg
                width="16"
                height="10"
                viewBox="0 0 30 30"
              >
                <use xlink:href="#icon-arrow-left" />
              </svg>
              <span>{{ keepBrowsingText }}</span>
            </div>
          </div>
          <div
            class="small-6 column cart__disabled-checkout-container-column"
            @click="showMinimumValueError"
            v-click-outside="hideMinimumValueError"
          >
            <button class="button cart__disabled-checkout-btn">
              {{ checkoutText }}
            </button>
          </div>
        </div>
      </div>
      <div v-else>
        <div
          class="small-12 column cart__disabled-checkout-container-column"
          @click="showMinimumValueError"
          v-click-outside="hideMinimumValueError"
        >
          <a class="button cart__disabled-checkout-btn">
            {{ secureCheckoutText }}
          </a>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { getPrefix } from '../../../../../utils/prefix';
import Microcart from '../../../../../core/compatibility/components/blocks/Microcart/Microcart';
import { getCurrencyStr } from './../../../../../utils/currencyFormatting.js';
import { postTrackEvent } from '../../../../../utils/analytics';
import errorMessage from '../../../theme/blocks/errorMessage';
import clickOutside from '../../../../../shared/directives/clickOutside';

export default {
  name: 'CheckoutBtn',
  components: {
    errorMessage: errorMessage,
  },
  mixins: [Microcart],
  directives: {
    clickOutside,
  },
  data() {
    return {
      minimumValueError: false,
      checkoutBlocked: false,
      isMobile: false,
      checkoutDisabled: false,
      paymentRequestButtonId: 'payment-request-button',
      stripeElements: null,
      stripe: null,
      paymentRequest: null,
      paymentRequestButton: null,
      canMakePaymentResult: null,
      doActionPaymentResult: false,
      initialBelowMinimumThreshold: null,
      paymentIntent: null,
      paymentCancelled: false,
      instantPayFailed: false,
      instantPayMinOrderThreshold: 1,
      confirmingPayment: false,
      confirmedPayment: false
    };
  },
  props: {
    belowMinimumThreshold: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    total: {
      type: Number,
      required: true,
      default: () => 0,
    },
    totalToPay: {
      type: Number,
      required: true,
      default: () => 0,
    },
    loadingTotal: {
      type: Boolean,
      required: true,
      default: false,
    },
    allInStock: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    stockCheckComplete: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  computed: {
    ...mapGetters({
      cartMinimumOrderThreshold: 'cart/getMinimumOrderThreshold',
      cartPaymentRequest: 'cart/getPaymentRequest',
    }),
    checkoutBtnClass() {
      return {
        button: true,
        'button--with-icon': true,
        'cart__button-continue': true,
        'cart__link-disabled': this.checkoutBlocked,
      };
    },
    minimumOrderMessageClass() {
      return {
        'cart__minimum-order-message': true,
        'cart__minimum-order-message-hidden': this.minimumValueError,
      };
    },
    getCheckoutUrl() {
      return `${getPrefix()}checkout/validate_checkout/`;
    },
    getDifference() {
      return getCurrencyStr(
        this.cartMinimumOrderThreshold - this.getTotal,
        true
      );
    },
    getMinimumOrderThresholdWithCurrency() {
      return getCurrencyStr(this.cartMinimumOrderThreshold, true);
    },
    woohooText() {
      return PATCH_I18N.cart.woohoo;
    },
    yourCartText() {
      return PATCH_I18N.cart.your_bag;
    },
    secureCheckoutText() {
      return PATCH_I18N.cart.continute_checkout;
    },
    secureCheckoutTextShort() {
      return PATCH_I18N.cart.checkout;
    },
    keepBrowsingText() {
      return PATCH_I18N.cart.keep_browsing;
    },
    checkoutText() {
      return PATCH_I18N.cart.checkout;
    },
    minOrderValueWarning() {
      return PATCH_I18N.cart.min_order_value_warning
        .replace('[1]', this.getDifference)
        .replace('[2]', this.getMinimumOrderThresholdWithCurrency);
    },
    minOrderValueError() {
      return PATCH_I18N.cart.min_order_value_error
        .replace('[1]', this.getMinimumOrderThresholdWithCurrency)
        .replace('[2]', this.getDifference);
    },
    getTotal() {
      const total = this.totals.find(t => t.code === 'grand_total');
      if (total) {
        return total.value;
      }
      return null;
    },
    errorText() {
      if (this.checkoutDisabled) {
        return window.disableCheckoutText;
      } else {
        return this.minOrderValueError;
      }
    },
    showError() {
      return this.checkoutDisabled || this.minimumValueError;
    },
    normalCheckout() {
      return !(this.belowMinimumThreshold || this.checkoutDisabled);
    },
    showExpressCheckout() {
      return window.showExpressCheckout;
    },
    aboveMinInstantPayOrderThreshold() {
      return this.totalToPay >= this.instantPayMinOrderThreshold;
    }
  },
  watch: {
    totals: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        const oldTotal = this.getTotalValue(oldValue);
        const newTotal = this.getTotalValue(newValue);
        if (oldTotal !== newTotal) {
          // total has changed so check for instant pay button again
          this.stripeInstantPayButton();
        }
      },
    },
    loadingTotal: {
      immediate: true,
      handler(nv, ov) {
        // Using the loadingTotal form the parent component to set checkoutblocked
        if (nv === true) {
          this.blockCheckout();
        } else {
          this.unblockCheckout();
        }
      },
    },
    minimumValueError: {
      immediate: false,
      handler(newValue, oldValue) {
        /*
         * This will be used to watch the error message so if the message disappears
         * so newValue is false and oldValue is true it will hide show the Zendesk.
         */
        if (newValue === false && oldValue === true) {
          if (this.isMobile) {
            this.$store.dispatch('ui/showZendeskWidget');
          }
        }
      },
    },
    cartPaymentRequest: {
      immediate: true,
      handler(newValue, oldValue) {
        if (oldValue !== undefined) {
          // if the cartPaymentRequest object has changed it means it's been recreated
          // so we need to re-render this component
          this.$emit('rerender');
        }
      },
    },
  },
  beforeMount() {
    /*
     * Using the events to turn the checkout block on and off so should be
     */
    this.$bus.$on('cart-before-update', () => {
      this.checkoutBlocked = true;
    });
    this.$bus.$on('cart-after-updatetotals', () => {
      this.checkoutBlocked = false;
    });
  },
  mounted() {
    /*
     * This will check the size of the component and if matches the screen size then
     * it is a mobile and returns True else False
     */
    this.isMobile = this.$el.clientWidth === document.body.clientWidth;
    this.checkoutDisabled = window.disableCheckout;
    // initialise stripe instant pay
    const stripeTokenElement = document.getElementById('cartElement');
    const stripeToken = stripeTokenElement?.dataset.stripePublicKey;
    this.stripe = Stripe(stripeToken);
    this.stripeElements = this.stripe.elements();
    this.paymentRequest = this.cartPaymentRequest;
    this.createPaymentButton();
    this.stripeInstantPayButton();
  },
  methods: {
    blockCheckout(el) {
      this.checkoutBlocked = true;
    },
    unblockCheckout(el) {
      this.checkoutBlocked = false;
      if (this.doActionPaymentResult) {
        // stripe instant pay button is shown/hidden here as the element doesn't exist until checkout is unblocked
        this.showHideInstantPayButton(this.canMakePaymentResult);
        this.doActionPaymentResult = false;
      }
    },
    closeMicrocartExtend() {
      this.closeMicrocart();
      postTrackEvent('Keep browsing btn clicked', { value: true });
    },
    hideMinimumValueError(el) {
      this.minimumValueError = false;
    },
    getTotalValue(value) {
      if (value) {
        const total = value.find(t => t.code === 'grand_total');
        if (total) {
          return total.value;
        }
      }
      return null;
    },
    showMinimumValueError() {
      if (this.minimumValueError) {
        postTrackEvent('Order minimum value error shaking shown', {
          value: true,
        });
        const errorContainer = document.querySelector(
          '.cart__minimum-value-error-container'
        );
        errorContainer.classList.add(...['animated', 'shake']);

        errorContainer.addEventListener('animationend', () => {
          errorContainer.classList.remove('animated');
          errorContainer.classList.remove('shake');
        });
      }
      this.minimumValueError = true;
      if (this.isMobile) {
        this.$store.dispatch('ui/hideZendeskWidget');
      }
      postTrackEvent('Cart error', { error: 'Order minimum value error shown' });
    },
    instantPaymentFailed(result) {
      // display error to user and ask them to complete standard checkout
      this.instantPayFailed = true;
      this.confirmingPayment = false;
    },
    instantPaymentSuccess(result) {
      this.confirmingPayment = false;
      this.confirmedPayment = true;
      this.$emit('doInstantPaymentComplete', result);
    },
    onPaymentMethod(ev){
      // Confirm the PaymentIntent without handling potential next actions (yet).
      if (this.paymentIntent && this.paymentIntent.client_secret) {
        const clientSecret = this.paymentIntent.client_secret;
        if (!this.confirmingPayment && !this.confirmedPayment) {
          // ensure we only attempt one confirmCardPayment
          this.confirmingPayment = true;
          this.instantPayFailed = false;
          this.stripe.confirmCardPayment(
            clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          ).then((confirmResult) => {
            if (confirmResult.error) {
              // Report to the browser that the payment failed, prompting it to
              // re-show the payment interface, or show an error message and close
              // the payment interface.
              ev.complete('fail');
              this.instantPaymentFailed();
            } else {
              // Report to the browser that the confirmation was successful, prompting
              // it to close the browser payment method collection interface.
              ev.complete('success');
              // Check if the PaymentIntent requires any actions
              if (confirmResult.paymentIntent.status === 'requires_action') {
                // Let Stripe.js handle the rest of the payment flow.
                this.stripe.confirmCardPayment(clientSecret).then((result) => {
                  if (result.error) {
                    // The payment failed - ask customer to use standard checkout
                    this.instantPaymentFailed(result);
                    postTrackEvent('Instant payment failed', {
                      value: this.totalToPay,
                      reason: 'Payment required action and unable to authorise'
                    });
                  } else {
                    // The payment has succeeded.
                    this.instantPaymentSuccess(ev);
                  }
                });
              } else {
                // The payment has succeeded.
                this.instantPaymentSuccess(ev);
              }
            }
          });
        }
      } else {
        // something has gone wrong - ask customer to use standard checkout
        ev.complete('fail');
        this.instantPaymentFailed();
        postTrackEvent('Instant payment failed', {
          value: this.totalToPay,
          reason: 'Unable to get paymentIntent'
        });
      }
    },
    showHideInstantPayButton(result) {
      if (result) {
        this.paymentRequestButton.mount(`#${this.paymentRequestButtonId}`);
        this.$emit('eligibleEvent', result);
        this.paymentRequestButton.on('click', () => {
          this.paymentCancelled = false;
          this.instantPayFailed = false;
          postTrackEvent('Checkout Started', {
            method: 'instant_pay',
            value: this.totalToPay,
          });
          // send event to GTM
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'instant-pay-started' });
          this.$store.dispatch('cart/getPaymentIntent').then((result) => {
            this.paymentIntent = result;
          });
        });
        this.paymentRequest.on('paymentmethod', this.onPaymentMethod.bind(this));
        this.paymentRequest.on('cancel', () => {
          // handle instant pay cancel event
          postTrackEvent('Instant payment cancelled', {
            value: this.totalToPay,
          });
          this.paymentCancelled = true;
        });
      } else {
        const paymentRequestButtonElement = document.getElementById(this.paymentRequestButtonId);
        if (paymentRequestButtonElement) {
          paymentRequestButtonElement.style.display = 'none';
        }
      }
    },
    createPaymentButton() {
      // create a new paymentRequestButton
      this.paymentRequestButton = this.stripeElements.create('paymentRequestButton', {
        paymentRequest: this.paymentRequest,
        style: {
          paymentRequestButton: {
            height: '55px'
          },
        },
      });
    },
    stripeInstantPayButton() {
      this.paymentRequest = this.cartPaymentRequest;
      this.instantPayFailed = false;
      // the button is removed from the DOM if below threshold so will need to be remounted if changed (or on first load)
      const isThresholdChange = this.initialBelowMinimumThreshold === null || this.initialBelowMinimumThreshold !== this.belowMinimumThreshold;
      if (this.paymentRequest && this.paymentRequestButton && !this.belowMinimumThreshold && !this.checkoutDisabled && isThresholdChange) {
        this.initialBelowMinimumThreshold = this.belowMinimumThreshold;
        // Check the availability of the Payment Request API first.
        this.paymentRequest.canMakePayment().then((result) => {
          if (this.checkoutBlocked) {
            // if checkout is blocked, trigger an update on the unblocked event handler as otherwise
            // the payment request button doesn't exist in the DOM
            this.doActionPaymentResult = true;
            this.canMakePaymentResult = result;
          } else {
            this.showHideInstantPayButton(result);
          }
        });
      } else if (this.paymentRequest && this.paymentRequestButton) {
        this.initialBelowMinimumThreshold = this.belowMinimumThreshold;
      }
    }
  },
};
</script>
